import {
	fetchFileRequest,
	makeRequest,
	uploadFileRequest,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { CONTENT_TYPE, HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import {
	VerifyBankIdentifiersDto,
	VerifyBankIdentifiersRes,
	ApplyVestingStartDateDto,
	ApplyVestingTermDto,
	ApplyVestingTermToTransactionDto,
	SaveTransactionVestingDto,
	BankAccountDto,
	BulkActionPayloadDto,
	BulkArchiveDocumentWorkflowTemplatesDto,
	BulkDeleteDocumentWorkflowTemplatesRes,
	BulkDownloadFilesDto,
	BulkOperationResult,
	ArchiveKycFormTemplateDto,
	ArchiveProgrammeKycFormTemplateDto,
	CreateContractDto,
	CreateCustomDetailDto,
	CreateKycFormTemplateDto,
	CreateVestingTermDto,
	CreateVestingTermGradedDto,
	CreateVestingTermLeaverProvisionDto,
	CreateVestingTermStraightLineDto,
	DataBankAccountTct,
	DataContractTct,
	DataCustomDetailTct,
	DataFileTct,
	DataJoinerInvestorGetTemplateDto,
	DeleteKycFormTemplatesDto,
	DeleteProgrammeKycFormTemplatesDto,
	KycFormTemplateInstanceCountRes,
	KycFormTemplateRes,
	DataLoanDefinitionTct,
	DataMipTct,
	EmailWorkflowProgressRes,
	GetInvestorDraftRes,
	GetInvestorsPivDetailsRes,
	GetMipDraftStepStatusRes,
	InvestorBulkActionDto,
	InvestorDraftDto,
	InvestorInviteRes,
	InvestorMessagesRes,
	InvestorProcessSettingsDto,
	LoanDefinitionDto,
	PatchDeclarationsDto,
	PostInvestorDraftRes,
	PostInvestorJoinerParseTemplateRes,
	PostInvestorLeaverParseTemplateRes,
	PostJoinerAdditionalAllocationMIPDto,
	PostJoinerAllocationsRes,
	PostLeaverNegativeAllocationMIPDto,
	PostLeaverNegativeAllocationsRes,
	ReleaseWorkflowProgressRes,
	RemoveVestingTermDto,
	RemoveVestingTermFromTransactionDto,
	UpdateAcceptanceStatusDto,
	UpdateArchivedDocumentWorkflowTemplateDto,
	UpdateCustomDetailDto,
	UpdateKycFileDto,
	UpdateKycFormTemplateDto,
	UpdateKycSignaturePlacementDto,
	UpdateMIPDataDto,
	UpdateMIPDraftGeneralDealInputsDto,
	UpdateMipIsKycRequiredDto,
	UpdateVestingTermDto,
	UpdateVestingTermGradedDto,
	UpdateVestingTermLeaverProvisionDto,
	UpdateVestingTermStraightLineDto,
	VestingTermGradedRes,
	VestingTermLeaverProvisionRes,
	VestingTermRes,
	VestingTermSimplifiedRes,
	VestingTermStraightLineRes,
	GetAllDocumentWorkflowTemplatesRes,
	DealDocumentTemplateSummaryRes,
	DocumentWorkflowTemplateInfo,
	CreateDocumentWorkflowTemplateDto,
	DocumentWorkflowTemplateVisibleIn,
	DownloadInvestmentsExcelDto,
	PerformanceVestingTermRes,
	UpsertPerformanceVestingDto,
	CreateDocumentWorkflowsDto,
	DocumentSigningSecurityLevel,
	GetSignatureSlotsRes,
	SignatureSlotRes,
	SignaturePlacementDto,
	DatePlacementDto,
	CurrencyType,
	SendMasterDocumentsDto,
	SendMasterDocumentsRes,
	BulkUpdateExpectedTransactionTypeDto,
	BulkUpdateExpectedTransactionTypeRes,
	CustomDetailCategory,
	CustomDetailEnum,
	KycLibraryTemplateRes,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { ServiceRequestOptions } from '@dealstack/frontend/shared/other/react/ApiWrapper/types';
import { CombinedMessagesFiltersDto } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useMessages.types';
import { GetMasterDocumentsListParams } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/types.ts';
import {
	FieldBindingCatalogueRes,
	FieldBindingDto,
	FieldBindingRes,
	PreviewRes,
} from '@dealstack/frontend/shared/components/FieldBinding/fieldBinding.types.ts';
import type { DocxFlowDocumentRes } from '@dealstack/shared/lib/generated';
import { QueryFunctionContext } from '@tanstack/react-query';

export const getVestingTerms = async (endpoint: string, signal?: AbortSignal) => {
	return await makeRequest<ApiV1.Response<VestingTermRes[]>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
};
export const getVestingTermsSimplified = async (endpoint: string, signal?: AbortSignal) => {
	return await makeRequest<ApiV1.Response<VestingTermSimplifiedRes[]>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
};

export const createVestingTerm = async (args: CreateVestingTermDto) => {
	return await makeRequest<ApiV1.Response<VestingTermSimplifiedRes>>({
		endpoint: API_PATHS.MIP_VESTING_TERM_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	});
};

export type UpdateVestingTermArgs = {
	id: string;
} & UpdateVestingTermDto;

export const updateVestingTerm = async (args: UpdateVestingTermArgs) => {
	return await makeRequest<ApiV1.Response<VestingTermSimplifiedRes>>({
		endpoint: formatUnicorn(API_PATHS.MIP_VESTING_TERM_ID_PATCH, {
			id: args.id,
		}),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify({
			...args,
		}),
	});
};

export const deleteVestingTerm = async (args: { id: string }) => {
	await makeRequest({
		endpoint: formatUnicorn(API_PATHS.MIP_VESTING_TERM_ID_DELETE, {
			id: args.id,
		}),
		method: HTTP_METHOD.DELETE,
	});
};

export const createVestingTermLeaverProvision = async (
	args: CreateVestingTermLeaverProvisionDto
) => {
	return await makeRequest<ApiV1.Response<VestingTermLeaverProvisionRes>>({
		endpoint: API_PATHS.MIP_VESTING_TERM_LEAVER_PROVISION_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	});
};

export type UpdateLeaverProvisionArgs = {
	leaverProvisionId: string;
} & UpdateVestingTermLeaverProvisionDto;

export const updateVestingTermLeaverProvision = async (args: UpdateLeaverProvisionArgs) => {
	return await makeRequest<ApiV1.Response<VestingTermLeaverProvisionRes>>({
		endpoint: formatUnicorn(API_PATHS.MIP_VESTING_TERM_LEAVER_PROVISION_ID_PUT, {
			id: args.leaverProvisionId,
		}),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(args),
	});
};

export const deleteVestingTermLeaverProvision = async (args: { leaverProvisionId: string }) => {
	await makeRequest({
		endpoint: formatUnicorn(API_PATHS.MIP_VESTING_TERM_LEAVER_PROVISION_ID_DELETE, {
			id: args.leaverProvisionId,
		}),
		method: HTTP_METHOD.DELETE,
	});
};

export const createVestingTermGraded = async (args: CreateVestingTermGradedDto) => {
	return await makeRequest<ApiV1.Response<VestingTermGradedRes>>({
		endpoint: API_PATHS.MIP_VESTING_TERM_GRADED_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	});
};

export type UpdateVestingTermGradedArgs = {
	gradedTermId: string;
} & UpdateVestingTermGradedDto;

export const updateVestingTermGraded = async (args: UpdateVestingTermGradedArgs) => {
	return await makeRequest<ApiV1.Response<VestingTermGradedRes>>({
		endpoint: formatUnicorn(API_PATHS.MIP_VESTING_TERM_GRADED_ID_PUT, {
			id: args.gradedTermId,
		}),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify({
			...args,
		}),
	});
};

export const deleteVestingTermGraded = async (args: { gradedTermId: string }) => {
	await makeRequest({
		endpoint: formatUnicorn(API_PATHS.MIP_VESTING_TERM_GRADED_ID_DELETE, {
			id: args.gradedTermId,
		}),
		method: HTTP_METHOD.DELETE,
	});
};

export const createVestingTermStraightLine = async (args: CreateVestingTermStraightLineDto) => {
	return await makeRequest<ApiV1.Response<VestingTermStraightLineRes>>({
		endpoint: API_PATHS.MIP_VESTING_TERM_STRAIGHT_LINE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	});
};

export type UpdateVestingTermStraightLineArgs = {
	straightLineTermId: string;
} & UpdateVestingTermStraightLineDto;

export const updateVestingTermStraightLine = async (args: UpdateVestingTermStraightLineArgs) => {
	return await makeRequest<ApiV1.Response<VestingTermStraightLineRes>>({
		endpoint: formatUnicorn(API_PATHS.MIP_VESTING_TERM_STRAIGHT_LINE_ID_PUT, {
			id: args.straightLineTermId,
		}),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify({
			...args,
		}),
	});
};

export const upsertPerformanceVesting = async (args: UpsertPerformanceVestingDto) => {
	return await makeRequest<ApiV1.Response<PerformanceVestingTermRes>>({
		endpoint: API_PATHS.MIP_VESTING_TERM_PERFORMANCE_VESTING_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	});
};

interface ICreateMIPDraftArgs {
	dealId: string;
}

export const createMIPDraft = async (
	_args: ICreateMIPDraftArgs,
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<DataMipTct>>(
		{
			endpoint: API_PATHS.MIP_DRAFT_POST,
			method: HTTP_METHOD.POST,
		},
		{
			...options,
		}
	);

	return response;
};

export const updateMip = async (args: UpdateMIPDataDto, options?: ServiceRequestOptions) => {
	const response = await makeRequest<ApiV1.Response<DataMipTct>>(
		{
			endpoint: API_PATHS.MIP_PUT,
			method: HTTP_METHOD.PUT,
			body: JSON.stringify({
				...args,
			}),
		},
		{
			...options,
		}
	);

	return response;
};

export interface MipProgrammeEntity {
	id: string;
	entityId: string;
	displayName: string;
	entityCurrency: CurrencyType | null;
	securitiesCount: number;
	canDelete: boolean;
	deleteBlockReason: string | null;
}

export const getMipProgrammeEntities = async () => {
	return await makeRequest<ApiV1.Response<MipProgrammeEntity[]>>({
		endpoint: '/mip/entities',
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload ?? []);
};

export const addMipProgrammeEntity = async (entityId: string) => {
	await makeRequest<ApiV1.Response<void>>({
		endpoint: '/mip/entities',
		method: HTTP_METHOD.POST,
		body: JSON.stringify({ entityId }),
	});
};

export const deleteMipProgrammeEntity = async (entityId: string) => {
	await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn('/mip/entities/{entityId}', { entityId }),
		method: HTTP_METHOD.DELETE,
	});
};

export const updateMIPDraftGeneralDealInputs = async (
	args: UpdateMIPDraftGeneralDealInputsDto,
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<DataMipTct>>(
		{
			endpoint: API_PATHS.MIP_DRAFT_PUT,
			method: HTTP_METHOD.PUT,
			body: JSON.stringify({
				...args,
			}),
		},
		{
			...options,
		}
	);

	return response;
};

export const deleteMipDraft = async () => {
	return await makeRequest<ApiV1.Response<DataMipTct>>({
		endpoint: API_PATHS.MIP_DRAFT_DELETE,
		method: HTTP_METHOD.DELETE,
	});
};

export const getMIPDetailsDataForJoiner = async (_args: void, options?: ServiceRequestOptions) => {
	const response = await makeRequest<ApiV1.Response<DataMipTct>>(
		{
			endpoint: API_PATHS.MIP_JOINER_GET,
			method: HTTP_METHOD.GET,
		},
		{
			...options,
		}
	);

	return response;
};

export const getMIPDetailsDataForLeaver = async (_args: void, options?: ServiceRequestOptions) => {
	const response = await makeRequest<ApiV1.Response<DataMipTct>>(
		{
			endpoint: API_PATHS.MIP_LEAVER_GET,
			method: HTTP_METHOD.GET,
		},
		{
			...options,
		}
	);

	return response;
};

export const getMIPDraftStepsStatuses = async (_args: void, options?: ServiceRequestOptions) => {
	const response = await makeRequest<ApiV1.Response<GetMipDraftStepStatusRes[]>>(
		{
			endpoint: API_PATHS.MIP_DRAFT_STEPS_STATUSES_GET,
			method: HTTP_METHOD.GET,
		},
		{
			...options,
		}
	);

	return response;
};

export const getMIPBankAccountData = async (_args: void, options?: ServiceRequestOptions) => {
	const response = await makeRequest<ApiV1.Response<DataBankAccountTct>>(
		{
			endpoint: API_PATHS.MIP_BANK_ACCOUNT_GET,
			method: HTTP_METHOD.GET,
		},
		{
			...options,
		}
	);

	return response;
};

export const updateMIPBankAccountData = async (
	args: { body: BankAccountDto; params: { bankAccountId: string } },
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<DataBankAccountTct>>(
		{
			endpoint: formatUnicorn(API_PATHS.MIP_BANK_ACCOUNT_ID_PUT, {
				id: args.params.bankAccountId,
			}),
			method: HTTP_METHOD.PUT,
			body: JSON.stringify(args.body),
		},
		{
			...options,
		}
	);

	return response;
};

export const createMIPBankAccountData = async (
	args: BankAccountDto,
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<DataBankAccountTct>>(
		{
			endpoint: API_PATHS.MIP_BANK_ACCOUNT_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify(args),
		},
		{
			...options,
		}
	);

	return response;
};

export const validateMIPBankAccount = async (
	args: { bankAccountId: string; body: VerifyBankIdentifiersDto },
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<VerifyBankIdentifiersRes>>(
		{
			endpoint: formatUnicorn(API_PATHS.MIP_BANK_ACCOUNT_ID_VALIDATE_POST, {
				id: args.bankAccountId,
			}),
			method: HTTP_METHOD.POST,
			body: JSON.stringify(args.body),
		},
		{
			...options,
		}
	);

	return response;
};

export const deleteMIPBankAccountData = async (
	args: { bankAccountId: string },
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<void>>(
		{
			endpoint: formatUnicorn(API_PATHS.MIP_BANK_ACCOUNT_ID_DELETE, {
				id: args.bankAccountId,
			}),
			method: HTTP_METHOD.DELETE,
		},
		{
			...options,
		}
	);

	return response;
};

export const downloadUpsertSharesInvestorsExcelTemplate = async (
	args: DataJoinerInvestorGetTemplateDto | Record<string, never>,
	options?: ServiceRequestOptions
) => {
	const { investorId, bulkAction, pendingSecurities = [], loansDefinitions = [] } = args;
	const selectedInvestorsResponse = await makeRequest<Blob>(
		{
			endpoint: API_PATHS.MIP_INVESTOR_JOINER_TEMPLATE_POST,
			method: HTTP_METHOD.POST,
			customHeaders: {
				Accept: CONTENT_TYPE.EXCEL,
			},
			body: JSON.stringify({
				investorId: investorId ?? [],
				bulkAction,
				pendingSecurities,
				loansDefinitions,
			}),
		},
		{
			...options,
		}
	);
	return selectedInvestorsResponse;
};

export const downloadInvestorsExcelTemplate = async (
	args: DataJoinerInvestorGetTemplateDto | Record<string, never>
) => {
	const { investorId = [], pendingSecurities = [], loansDefinitions = [] } = args;
	const selectedInvestorsResponse = await makeRequest<Blob>({
		endpoint: API_PATHS.MIP_INVESTOR_JOINER_TEMPLATE_POST,
		method: HTTP_METHOD.POST,
		customHeaders: {
			Accept: CONTENT_TYPE.EXCEL,
		},
		body: JSON.stringify({
			investorId,
			pendingSecurities,
			loansDefinitions,
		}),
	});
	return selectedInvestorsResponse;
};

export const downloadLeaversInvestorsExcelTemplate = async (
	args: {
		investorId?: string[];
		securities?: string[];
		loansDefinitions?: string[];
		bulkAction?: InvestorBulkActionDto;
	},
	options?: ServiceRequestOptions
) => {
	const { investorId = [], securities = [], loansDefinitions = [], bulkAction } = args;
	const selectedInvestorsResponse = await makeRequest<Blob>(
		{
			endpoint: API_PATHS.MIP_INVESTOR_LEAVER_TEMPLATE_POST,
			method: HTTP_METHOD.POST,
			customHeaders: {
				Accept: CONTENT_TYPE.EXCEL,
			},
			body: JSON.stringify({
				investorId,
				bulkAction,
				securities,
				loansDefinitions,
			}),
		},
		{
			...options,
		}
	);
	return selectedInvestorsResponse;
};

export const uploadJoinerInvestorsExcel = async ({
	formData,
	onUploadProgress,
}: {
	formData: FormData;
	onUploadProgress?: (percent: number) => void;
}) => {
	const response = await uploadFileRequest<ApiV1.Response<PostInvestorJoinerParseTemplateRes>>(
		{
			endpoint: API_PATHS.MIP_INVESTOR_JOINER_PARSE_TEMPLATE_POST,
			method: HTTP_METHOD.POST,
		},
		formData,
		onUploadProgress
	);
	return response;
};

export const uploadLeaverInvestorsExcel = async ({
	formData,
	onUploadProgress,
}: {
	formData: FormData;
	onUploadProgress?: (percent: number) => void;
}) => {
	const response = await uploadFileRequest<ApiV1.Response<PostInvestorLeaverParseTemplateRes>>(
		{
			endpoint: API_PATHS.MIP_INVESTOR_LEAVER_PARSE_TEMPLATE_POST,
			method: HTTP_METHOD.POST,
		},
		formData,
		onUploadProgress
	);
	return response;
};

export const getMIPInvestorsDraft = async () => {
	const response = await makeRequest<ApiV1.Response<GetInvestorDraftRes>>({
		endpoint: API_PATHS.MIP_INVESTOR_DRAFT_GET,
		method: HTTP_METHOD.GET,
	});

	return response;
};

export const applyVestingTerm = async (args: ApplyVestingTermDto) => {
	await makeRequest({
		endpoint: API_PATHS.MIP_VESTING_TERM_APPLY_TO_INVESTMENTWORKFLOW_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify({
			...args,
		}),
	});
};

export const removeVestingTerm = async (args: RemoveVestingTermDto) => {
	await makeRequest({
		endpoint: API_PATHS.MIP_VESTING_TERM_REMOVE_FROM_INVESTMENTWORKFLOW_DELETE,
		method: HTTP_METHOD.DELETE,
		body: JSON.stringify({
			...args,
		}),
	});
};

export const applyVestingStartDateToInvestmentWorkflow = async (
	args: ApplyVestingStartDateDto & { investmentWorkflowId: string }
) => {
	const { investmentWorkflowId, ...rest } = args;
	await makeRequest({
		endpoint: formatUnicorn(
			API_PATHS.MIP_VESTING_TERM_VESTING_START_INVESTMENTWORKFLOW_INVESTMENTWORKFLOWID_POST,
			{ investmentWorkflowId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify({
			...rest,
		}),
	});
};

export const applyVestingStartDateToTransaction = async (
	args: ApplyVestingStartDateDto & { transactionId: string }
) => {
	const { transactionId, ...rest } = args;
	await makeRequest({
		endpoint: formatUnicorn(
			API_PATHS.MIP_VESTING_TERM_VESTING_START_TRANSACTION_TRANSACTIONID_POST,
			{ transactionId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify({
			...rest,
		}),
	});
};

export const applyVestingTermToTransaction = async (args: ApplyVestingTermToTransactionDto) => {
	await makeRequest({
		endpoint: API_PATHS.MIP_VESTING_TERM_APPLY_TO_TRANSACTION_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify({
			...args,
		}),
	});
};

export const saveTransactionVesting = async ({
	transactionId,
	...body
}: SaveTransactionVestingDto & { transactionId: string }) => {
	await makeRequest({
		endpoint: formatUnicorn(API_PATHS.MIP_VESTING_TERM_TRANSACTION_TRANSACTIONID_PUT, {
			transactionId,
		}),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(body),
	});
};

export const removeVestingTermFromTransaction = async (
	args: RemoveVestingTermFromTransactionDto
) => {
	await makeRequest({
		endpoint: API_PATHS.MIP_VESTING_TERM_REMOVE_FROM_TRANSACTION_DELETE,
		method: HTTP_METHOD.DELETE,
		body: JSON.stringify({
			...args,
		}),
	});
};

export const updateMIPDraftInvestors = async (args: InvestorDraftDto) => {
	await makeRequest({
		endpoint: API_PATHS.MIP_INVESTOR_DRAFT_PUT,
		method: HTTP_METHOD.PUT,
		body: JSON.stringify({
			...args,
		}),
	});
};

export const getMIPContractsQuery = async (endpoint: string, signal?: AbortSignal) => {
	return await makeRequest<ApiV1.Response<DataContractTct[]>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
};

export const getDocumentWorkflowDraftTemplates = async (
	args: {
		dealId: string;
		programmeId: string;
		visibleIn?: DocumentWorkflowTemplateVisibleIn[];
	},
	options?: ServiceRequestOptions
) => {
	const endpoint = formatUnicorn(
		API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_DRAFT_TEMPLATES_GET,
		{
			dealId: args.dealId,
			programmeId: args.programmeId,
		}
	);
	const response = await makeRequest<ApiV1.Response<DataContractTct[]>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
			queryString: args.visibleIn !== undefined ? { visibleIn: args.visibleIn } : undefined,
		},
		{ ...options }
	);
	return response;
};

export const getMIPPersonalCustomDetails = async (_args: void, options?: ServiceRequestOptions) => {
	const response = await makeRequest<ApiV1.Response<DataCustomDetailTct[]>>(
		{
			endpoint: API_PATHS.MIP_CUSTOM_DETAIL_GET,
			method: HTTP_METHOD.GET,
		},
		{
			...options,
		}
	);

	return response;
};

export const getMIPKycFormsTemplates = async (
	args?: { archived?: boolean },
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<KycFormTemplateRes[]>>(
		{
			endpoint: API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_GET,
			method: HTTP_METHOD.GET,
			queryString: args?.archived !== undefined ? { archived: args.archived } : undefined,
		},
		{
			...options,
		}
	);

	return response;
};

/**
 * The published library templates tagged for one section of Tax & Compliance. A missing
 * `category` means the "Other" section, which is a real section rather than "any category".
 */
export const getKycLibraryTemplates = async (
	args: { type: CustomDetailEnum; category?: CustomDetailCategory | null },
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<KycLibraryTemplateRes[]>>(
		{
			endpoint: API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_LIBRARY_GET,
			method: HTTP_METHOD.GET,
			queryString: {
				type: args.type,
				...(args.category ? { category: args.category } : {}),
			},
		},
		{ ...options }
	);

	return response.payload ?? [];
};

export const updateMipKycRequired = async ({ body }: { body: UpdateMipIsKycRequiredDto }) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: API_PATHS.MIP_KYC_REQUIRED_PUT,
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(body),
	});
};

export const createMIPPersonalCustomDetails = async (
	args: CreateCustomDetailDto,
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest(
		{
			endpoint: API_PATHS.MIP_CUSTOM_DETAIL_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify({
				...args,
			}),
		},
		{
			...options,
		}
	);
	return response;
};

export type IUpdateMIPPersonalCustomDetailsArgs = {
	customDetailId: string;
} & UpdateCustomDetailDto;
export const updateMIPPersonalCustomDetails = async (
	args: IUpdateMIPPersonalCustomDetailsArgs,
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest(
		{
			endpoint: formatUnicorn(API_PATHS.MIP_CUSTOM_DETAIL_ID_PUT, {
				id: args.customDetailId,
			}),
			method: HTTP_METHOD.PUT,
			body: JSON.stringify({
				...args,
			}),
		},
		{
			...options,
		}
	);
	return response;
};

export const createKycFormsTemplates = async (
	args: { customDetails: CreateKycFormTemplateDto[] },
	options?: ServiceRequestOptions
) => {
	return await Promise.all(
		args.customDetails.map((template) =>
			makeRequest(
				{
					endpoint: API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_POST,
					method: HTTP_METHOD.POST,
					body: JSON.stringify(template),
				},
				{
					...options,
				}
			)
		)
	);
};

export const updateKycFormsTemplates = async (
	args: { customDetails: Array<{ templateId: string } & UpdateKycFormTemplateDto> },
	options?: ServiceRequestOptions
) => {
	return await Promise.all(
		args.customDetails.map(({ templateId, ...body }) =>
			makeRequest(
				{
					endpoint: formatUnicorn(
						API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_TEMPLATEID_PUT,
						{ templateId }
					),
					method: HTTP_METHOD.PUT,
					body: JSON.stringify(body),
				},
				{
					...options,
				}
			)
		)
	);
};

export const deleteCustomDetail = async (
	args: {
		customDetailId: string;
	},
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest(
		{
			endpoint: formatUnicorn(API_PATHS.MIP_CUSTOM_DETAIL_ID_DELETE, {
				id: args.customDetailId,
			}),
			method: HTTP_METHOD.DELETE,
		},
		{
			...options,
		}
	);
	return response;
};

export const archiveKycFormsTemplate = async (
	args: { templateId: string } & ArchiveProgrammeKycFormTemplateDto,
	options?: ServiceRequestOptions
) => {
	return await makeRequest(
		{
			endpoint: formatUnicorn(
				API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_TEMPLATEID_ARCHIVE_PUT,
				{ templateId: args.templateId }
			),
			method: HTTP_METHOD.PUT,
			body: JSON.stringify({
				archived: args.archived,
				applyToInstances: args.applyToInstances,
			}),
		},
		{
			...options,
		}
	);
};

/** How many KYC workflows hold an instance of the form (mip-id header supplies the programme). */
export const getKycFormTemplateInstanceCount = async (templateId: string): Promise<number> => {
	const response = await makeRequest<ApiV1.Response<KycFormTemplateInstanceCountRes>>({
		endpoint: formatUnicorn(
			API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_TEMPLATEID_INSTANCE_COUNT_GET,
			{ templateId }
		),
		method: HTTP_METHOD.GET,
	});
	return response.payload?.count ?? 0;
};

export const deleteKycFormsTemplates = async (
	args: DeleteProgrammeKycFormTemplatesDto,
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest(
		{
			endpoint: API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_DELETE,
			method: HTTP_METHOD.DELETE,
			body: JSON.stringify(args),
		},
		{
			...options,
		}
	);
	return response;
};

export const getOrganizationKycFormsTemplates = async (
	organizationId: string,
	args?: { archived?: boolean },
	options?: ServiceRequestOptions
) => {
	return await makeRequest<ApiV1.Response<KycFormTemplateRes[]>>(
		{
			endpoint: formatUnicorn(
				API_PATHS.PROGRAMME_DEFAULTS_TAX_COMPLIANCE_ORGANIZATIONID_GET,
				{ organizationId }
			),
			method: HTTP_METHOD.GET,
			queryString: args?.archived !== undefined ? { archived: args.archived } : undefined,
		},
		{ ...options }
	);
};

export const createOrganizationKycFormsTemplates = async (
	organizationId: string,
	args: { customDetails: CreateKycFormTemplateDto[] },
	options?: ServiceRequestOptions
) => {
	return await Promise.all(
		args.customDetails.map((template) =>
			makeRequest(
				{
					endpoint: formatUnicorn(
						API_PATHS.PROGRAMME_DEFAULTS_TAX_COMPLIANCE_ORGANIZATIONID_POST,
						{ organizationId }
					),
					method: HTTP_METHOD.POST,
					body: JSON.stringify(template),
				},
				{ ...options }
			)
		)
	);
};

export const updateOrganizationKycFormsTemplates = async (
	organizationId: string,
	args: { customDetails: Array<{ templateId: string } & UpdateKycFormTemplateDto> },
	options?: ServiceRequestOptions
) => {
	return await Promise.all(
		args.customDetails.map(({ templateId, ...body }) =>
			makeRequest(
				{
					endpoint: formatUnicorn(
						API_PATHS.PROGRAMME_DEFAULTS_TAX_COMPLIANCE_ORGANIZATIONID_TEMPLATEID_PUT,
						{ organizationId, templateId }
					),
					method: HTTP_METHOD.PUT,
					body: JSON.stringify(body),
				},
				{ ...options }
			)
		)
	);
};

export const archiveOrganizationKycFormsTemplate = async (
	organizationId: string,
	args: { templateId: string } & ArchiveKycFormTemplateDto,
	options?: ServiceRequestOptions
) => {
	return await makeRequest(
		{
			endpoint: formatUnicorn(
				API_PATHS.PROGRAMME_DEFAULTS_TAX_COMPLIANCE_ORGANIZATIONID_TEMPLATEID_ARCHIVE_PUT,
				{ organizationId, templateId: args.templateId }
			),
			method: HTTP_METHOD.PUT,
			body: JSON.stringify({ archived: args.archived }),
		},
		{ ...options }
	);
};

export const deleteOrganizationKycFormsTemplates = async (
	organizationId: string,
	args: DeleteKycFormTemplatesDto,
	options?: ServiceRequestOptions
) => {
	return await makeRequest(
		{
			endpoint: formatUnicorn(
				API_PATHS.PROGRAMME_DEFAULTS_TAX_COMPLIANCE_ORGANIZATIONID_DELETE,
				{ organizationId }
			),
			method: HTTP_METHOD.DELETE,
			body: JSON.stringify(args),
		},
		{ ...options }
	);
};

export const getOrganizationKycSignatureSlots = async (
	organizationId: string,
	templateId: string
): Promise<SignatureSlotRes[]> => {
	const response = await makeRequest<ApiV1.Response<GetSignatureSlotsRes>>({
		endpoint: formatUnicorn(
			API_PATHS.PROGRAMME_DEFAULTS_TAX_COMPLIANCE_ORGANIZATIONID_TEMPLATEID_SIGNATURE_SLOTS_GET,
			{ organizationId, templateId }
		),
		method: HTTP_METHOD.GET,
	});
	return response.payload?.slots ?? [];
};

export const updateOrganizationKycSignaturePlacement = async (
	organizationId: string,
	templateId: string,
	body: UpdateKycSignaturePlacementDto,
	options?: ServiceRequestOptions
) =>
	await makeRequest(
		{
			endpoint: formatUnicorn(
				API_PATHS.PROGRAMME_DEFAULTS_TAX_COMPLIANCE_ORGANIZATIONID_TEMPLATEID_SIGNATURE_PLACEMENT_PUT,
				{ organizationId, templateId }
			),
			method: HTTP_METHOD.PUT,
			body: JSON.stringify(body),
		},
		{ ...options }
	);

/** Creates the programme ACTIVE, skipping the wizard. The deal comes from the `deal-id` header. */
export const createActiveProgramme = async (
	args: { programmeName: string; entityId?: string; currency: CurrencyType },
	options?: ServiceRequestOptions
) => {
	return await makeRequest<ApiV1.Response<DataMipTct>>(
		{
			endpoint: API_PATHS.MIP_DRAFT_ACTIVE_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify(args),
		},
		{ ...options }
	);
};

export const getMIPContracts = async (_args: void, options?: ServiceRequestOptions) => {
	const response = await makeRequest<ApiV1.Response<DataContractTct[]>>(
		{ endpoint: API_PATHS.MIP_CONTRACT_DRAFT_GET, method: HTTP_METHOD.GET },
		{ ...options }
	);
	return response;
};
export const createMIPContract = async (
	args: CreateContractDto,
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest(
		{
			endpoint: API_PATHS.MIP_CONTRACT_DRAFT_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify({
				...args,
			}),
		},
		{
			...options,
		}
	);

	return response;
};
export const updateMIPContract = async (
	{ id, ...body }: CreateContractDto & { id: string },
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest(
		{
			endpoint: formatUnicorn(API_PATHS.MIP_CONTRACT_DRAFT_ID_PUT, { id }),
			method: HTTP_METHOD.PUT,
			body: JSON.stringify(body),
		},
		{
			...options,
		}
	);
	return response;
};
export const deleteMIPContract = async (args: { id: string }, options?: ServiceRequestOptions) => {
	const response = await makeRequest(
		{
			endpoint: formatUnicorn(API_PATHS.MIP_CONTRACT_DRAFT_ID_DELETE, {
				id: args.id,
			}),
			method: HTTP_METHOD.DELETE,
		},
		{
			...options,
		}
	);
	return response;
};

export const uploadFile = async ({
	formData,
	onUploadProgress,
}: {
	formData: FormData;
	onUploadProgress?: (percent: number) => void;
}) => {
	const response = await uploadFileRequest<ApiV1.Response<DataFileTct>>(
		{
			endpoint: API_PATHS.FILE_POST,
			method: HTTP_METHOD.POST,
		},
		formData,
		onUploadProgress
	);
	return response;
};

// this for future add we can use
export const uploadFileV2 = async (file: File) => {
	const formData = new FormData();
	formData.append('file', file);
	formData.append('fileSize', file.size.toString());

	return await uploadFileRequest<ApiV1.Response<DataFileTct>>(
		{
			endpoint: API_PATHS.FILE_POST,
			method: HTTP_METHOD.POST,
		},
		formData
	);
};

export const getFileReactQuery = async (args: { id: string }, signal?: AbortSignal) => {
	const endpoint = formatUnicorn(API_PATHS.FILE_ID_GET, { id: args.id });
	return await fetchFileRequest(endpoint, { abortSignal: signal });
};

export const bulkDownloadFiles = async (
	args: BulkDownloadFilesDto,
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<Blob>(
		{
			endpoint: API_PATHS.FILE_BULK_DOWNLOAD_POST,
			method: HTTP_METHOD.POST,
			customHeaders: {
				Accept: CONTENT_TYPE.ZIP,
			},
			body: JSON.stringify(args),
		},
		{ ...options }
	);
	return response;
};

export const completeMipCreation = async (_args: void, options?: ServiceRequestOptions) => {
	const response = await makeRequest<ApiV1.Response<unknown>>(
		{ endpoint: API_PATHS.MIP_DRAFT_COMPLETE_GET, method: HTTP_METHOD.GET },
		{ ...options }
	);
	return response;
};

export type UpdateKycFileStatusProps = {
	kycId: string;
	body: UpdateAcceptanceStatusDto;
	options?: ServiceRequestOptions;
};
export const updateKycFileStatus = async (args: UpdateKycFileStatusProps) => {
	await makeRequest(
		{
			endpoint: formatUnicorn(API_PATHS.MIP_KYC_KYC_FILE_ID_PATCH, {
				id: args.kycId,
			}),
			method: HTTP_METHOD.PATCH,
			body: JSON.stringify(args.body),
		},
		{
			...args.options,
		}
	);
};

export const resendKycFileCertifierInvitation = async (kycFileId: string) => {
	await makeRequest({
		endpoint: formatUnicorn(API_PATHS.MIP_KYC_KYC_FILE_ID_CERTIFICATION_RESEND_POST, {
			id: kycFileId,
		}),
		method: HTTP_METHOD.POST,
	});
};

export const overrideKycStatusWithNewFile = async (
	args: { kycId: string; body: UpdateKycFileDto },
	options?: ServiceRequestOptions
) => {
	await makeRequest(
		{
			endpoint: formatUnicorn(API_PATHS.MIP_KYC_KYC_FILE_ID_PUT, {
				id: args.kycId,
			}),
			method: HTTP_METHOD.PUT,
			body: JSON.stringify(args.body),
		},
		{
			...options,
		}
	);
};

export type UpdateInvestorDeclarationsStatusProps = {
	investorId: string;
	body: UpdateAcceptanceStatusDto;
	kycWorkflowId?: string;
	options?: ServiceRequestOptions;
};

export const updateInvestorDeclarationsStatus = async (
	args: UpdateInvestorDeclarationsStatusProps
) => {
	await makeRequest(
		{
			endpoint: formatUnicorn(API_PATHS.MIP_KYC_INVESTORID_DECLARATIONS_PATCH, {
				investorId: args.investorId,
			}),
			method: HTTP_METHOD.PATCH,
			body: JSON.stringify(args.body),
			queryString: args.kycWorkflowId ? { kycWorkflowId: args.kycWorkflowId } : undefined,
		},
		{
			...args.options,
		}
	);
};

export type UpdateInvestorDeclarationsProps = {
	investorId: string;
	body: PatchDeclarationsDto;
	kycWorkflowId?: string;
	options?: ServiceRequestOptions;
};

export const updateInvestorDeclarations = async (args: UpdateInvestorDeclarationsProps) => {
	await makeRequest(
		{
			endpoint: formatUnicorn(API_PATHS.MIP_KYC_INVESTORID_DECLARATIONS_PUT, {
				investorId: args.investorId,
			}),
			method: HTTP_METHOD.PUT,
			body: JSON.stringify(args.body),
			queryString: args.kycWorkflowId ? { kycWorkflowId: args.kycWorkflowId } : undefined,
		},
		{
			...args.options,
		}
	);
};

export const postLeaverData = async (
	args: PostLeaverNegativeAllocationMIPDto,
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<PostLeaverNegativeAllocationsRes>>(
		{
			endpoint: API_PATHS.MIP_LEAVER_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify(args),
		},
		{ ...options }
	);
	return response;
};

export const postJoinersOrAllocationsData = async (
	args: PostJoinerAdditionalAllocationMIPDto,
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<PostJoinerAllocationsRes>>(
		{
			endpoint: API_PATHS.MIP_JOINER_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify(args),
		},
		{ ...options }
	);
	return response;
};

export const updateInvestorProcessSettings = async (args: {
	investorId: string;
	body: Partial<InvestorProcessSettingsDto>;
	kycWorkflowId?: string;
}) => {
	const path = formatUnicorn(API_PATHS.MIP_KYC_MORE_PIV_DOCUMENTS_INVESTORID_PATCH, {
		investorId: args.investorId,
	});
	const endpoint = args.kycWorkflowId
		? `${path}?kycWorkflowId=${encodeURIComponent(args.kycWorkflowId)}`
		: path;
	return await makeRequest({
		endpoint,
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify({ ...args.body }),
	});
};

export const uploadInvestorsExcel = async ({
	formData,
	onUploadProgress,
}: {
	formData: FormData;
	onUploadProgress?: (percent: number) => void;
}) => {
	const response = await uploadFileRequest<ApiV1.Response<PostInvestorDraftRes>>(
		{
			endpoint: API_PATHS.MIP_INVESTOR_DRAFT_TEMPLATE_POST,
			method: HTTP_METHOD.POST,
		},
		formData,
		onUploadProgress
	);
	return response;
};

export const uploadDocumentFile = async (
	{
		fileId,
	}: {
		fileId: string;
	},
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<unknown>>(
		{
			endpoint: API_PATHS.MIP_SIMPLE_DOCUMENT_WORKFLOW_DOCUMENT_FILE_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify({ fileId }),
		},
		{ ...options }
	);
	return response;
};

export const removeDocumentFile = async ({
	documentWorkflowFileId,
}: {
	documentWorkflowFileId: string;
}) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_SIMPLE_DOCUMENT_WORKFLOW_REMOVE_DOCUMENTWORKFLOWFILEID_DELETE,
			{
				documentWorkflowFileId,
			}
		),
		method: HTTP_METHOD.DELETE,
	});
};

export const sendMasterDocuments = async (
	args: SendMasterDocumentsDto,
	options?: ServiceRequestOptions
) => {
	return await makeRequest<ApiV1.Response<SendMasterDocumentsRes>>(
		{
			endpoint: API_PATHS.MIP_SIMPLE_DOCUMENT_WORKFLOW_SEND_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify({ ...args }),
		},
		{ ...options }
	);
};

export const simpleDocumentsWorkflowCreateDocuments = async (
	args: CreateDocumentWorkflowsDto,
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<InvestorInviteRes>>(
		{
			endpoint: API_PATHS.MIP_SIMPLE_DOCUMENT_WORKFLOW_CREATE_DOCUMENTS_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify({
				...args,
			}),
		},
		{
			...options,
		}
	);

	return response;
};

export const getLoanDefinitionDataByMipId = async (
	_args: void,
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<DataLoanDefinitionTct[]>>(
		{
			endpoint: API_PATHS.MIP_LOAN_DEFINITION_GET,
			method: HTTP_METHOD.GET,
		},
		{
			...options,
		}
	);

	return response;
};

export const createMIPLoanDefinition = async (
	args: { loansDefinitions: LoanDefinitionDto[] },
	options?: ServiceRequestOptions
): Promise<DataLoanDefinitionTct[]> => {
	return await makeRequest<ApiV1.Response<DataLoanDefinitionTct[]>>(
		{
			endpoint: API_PATHS.MIP_LOAN_DEFINITION_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify(args),
		},
		{ ...options }
	).then((response) => response.payload ?? []);
};

export const updateMIPLoanDefinitionData = async (
	args: { body: LoanDefinitionDto; loanDefinitionId: string },
	options?: ServiceRequestOptions
) => {
	await makeRequest(
		{
			endpoint: formatUnicorn(API_PATHS.MIP_LOAN_DEFINITION_ID_PUT, {
				id: args.loanDefinitionId,
			}),
			method: HTTP_METHOD.PUT,
			body: JSON.stringify({
				...args.body,
			}),
		},
		{
			...options,
		}
	);
};
export const deleteMIPLoanDefinitionData = async (
	args: {
		loanDefinitionId: string;
	},
	options?: ServiceRequestOptions
) => {
	await makeRequest(
		{
			endpoint: formatUnicorn(API_PATHS.MIP_LOAN_DEFINITION_ID_DELETE, {
				id: args.loanDefinitionId,
			}),
			method: HTTP_METHOD.DELETE,
		},
		{
			...options,
		}
	);
};

export const getPreFilledKycFormFile = async (
	args: { kycFormCustomDetailId: string },
	options?: ServiceRequestOptions
) => {
	const endpoint = formatUnicorn(
		API_PATHS.SHAREHOLDER_KYC_PRE_FILLED_FORM_FILE_KYCFORMCUSTOMDETAILID_GET,
		{ kycFormCustomDetailId: args.kycFormCustomDetailId }
	);

	return await fetchFileRequest(endpoint, options);
};

export const getInvestorEmailWorkflowProgress = async (endpoint: string, signal?: AbortSignal) => {
	const response = await makeRequest<ApiV1.Response<EmailWorkflowProgressRes>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);
	return response;
};

export const getInvestorReleaseWorkflowProgress = async (
	endpoint: string,
	signal?: AbortSignal
) => {
	const response = await makeRequest<ApiV1.Response<ReleaseWorkflowProgressRes>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);
	return response;
};

export const getPivDetailsForInvestors = async (
	endpoint: string,
	args: { mipId: string },
	signal?: AbortSignal
) => {
	const { mipId } = args;

	return await makeRequest<ApiV1.Response<GetInvestorsPivDetailsRes>>(
		{
			endpoint: formatUnicorn(endpoint, { mipId }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	).then((response) => response.payload);
};

export const getMessages = async (
	endpoint: string,
	filters: {
		filters: CombinedMessagesFiltersDto;
		investmentWorkflowId?: string;
		documentWorkflowInstanceId?: string;
	},
	pageParam: number,
	signal?: AbortSignal
) => {
	const pageSize = filters.filters?.pagination?.take ?? 25;
	const skip = pageParam ? pageParam * pageSize : 0;

	const response = await makeRequest<ApiV1.Response<InvestorMessagesRes>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
			queryString: {
				filters: {
					...filters.filters,
					pagination: {
						...filters.filters?.pagination,
						take: pageSize,
						skip,
					},
				},
				investmentWorkflowId: filters?.investmentWorkflowId,
				documentWorkflowInstanceId: filters?.documentWorkflowInstanceId,
			},
		},
		{
			abortSignal: signal,
		}
	);
	return response;
};

export const getAllDocumentWorkflowTemplatesList = async ({
	signal,
	queryKey,
}: QueryFunctionContext<[string, GetMasterDocumentsListParams]>) => {
	const [endpoint, params] = queryKey;
	return makeRequest<ApiV1.Response<GetAllDocumentWorkflowTemplatesRes>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
			queryString: params,
		},
		{ abortSignal: signal }
	);
};

export const getDocumentWorkflowTemplateById = async (
	endpoint: string
): Promise<DocumentWorkflowTemplateInfo> => {
	const response = await makeRequest<ApiV1.Response<DocumentWorkflowTemplateInfo>>({
		endpoint,
		method: HTTP_METHOD.GET,
	});
	const payload = response.payload;
	if (payload == null) {
		throw new Error('Document workflow template not found');
	}
	return payload;
};

export const updateDocumentWorkflowTemplateProperties = async (args: {
	endpoint: string;
	visibleIn?: DocumentWorkflowTemplateVisibleIn[];
	classifiers?: string[];
	signatureType?: string;
	instruction?: string;
	individualScheduleRequired?: boolean;
	individualInstances?: boolean;
	witnessRequired?: boolean;
	name?: string;
	counterparties?: Array<{
		name: string;
		numberOfSignaturesRequired: number;
		requiresWitness?: boolean;
	}>;
	useManualSignaturePlacement?: boolean;
	showStampMetadata?: boolean;
	signaturePlacements?: SignaturePlacementDto[];
	requiresPostDating?: boolean;
	datePlacements?: DatePlacementDto[];
}) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: args.endpoint,
		method: HTTP_METHOD.PUT,
		body: JSON.stringify({
			...(args.visibleIn !== undefined ? { visibleIn: args.visibleIn } : {}),
			...(args.classifiers !== undefined ? { classifiers: args.classifiers } : {}),
			...(args.signatureType !== undefined ? { signatureType: args.signatureType } : {}),
			...(args.instruction !== undefined ? { instruction: args.instruction } : {}),
			...(args.individualScheduleRequired !== undefined
				? { individualScheduleRequired: args.individualScheduleRequired }
				: {}),
			...(args.individualInstances !== undefined
				? { individualInstances: args.individualInstances }
				: {}),
			...(args.witnessRequired !== undefined
				? { witnessRequired: args.witnessRequired }
				: {}),
			...(args.name !== undefined ? { name: args.name } : {}),
			...(args.counterparties !== undefined ? { counterparties: args.counterparties } : {}),
			...(args.useManualSignaturePlacement !== undefined
				? { useManualSignaturePlacement: args.useManualSignaturePlacement }
				: {}),
			...(args.showStampMetadata !== undefined
				? { showStampMetadata: args.showStampMetadata }
				: {}),
			...(args.signaturePlacements !== undefined
				? { signaturePlacements: args.signaturePlacements }
				: {}),
			...(args.requiresPostDating !== undefined
				? { requiresPostDating: args.requiresPostDating }
				: {}),
			...(args.datePlacements !== undefined ? { datePlacements: args.datePlacements } : {}),
		}),
	});
};

/** The single placement slot for a KYC form template (mip-id header supplies the programme). */
export const getKycSignatureSlots = async (templateId: string): Promise<SignatureSlotRes[]> => {
	const response = await makeRequest<ApiV1.Response<GetSignatureSlotsRes>>({
		endpoint: formatUnicorn(
			API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_TEMPLATEID_SIGNATURE_SLOTS_GET,
			{ templateId }
		),
		method: HTTP_METHOD.GET,
	});
	return response.payload?.slots ?? [];
};

/** Save the manual signature placement for a persisted KYC form template. */
export const updateKycSignaturePlacement = async (
	templateId: string,
	body: UpdateKycSignaturePlacementDto,
	options?: ServiceRequestOptions
) =>
	await makeRequest(
		{
			endpoint: formatUnicorn(
				API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_TEMPLATEID_SIGNATURE_PLACEMENT_PUT,
				{ templateId }
			),
			method: HTTP_METHOD.PUT,
			body: JSON.stringify(body),
		},
		{ ...options }
	);

/** The coordinate field bindings of a PDF KYC form. */
export const getKycFieldBindings = async (templateId: string): Promise<FieldBindingRes[]> => {
	const response = await makeRequest<ApiV1.Response<FieldBindingRes[]>>({
		endpoint: formatUnicorn(
			API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_TEMPLATEID_FIELD_BINDINGS_GET,
			{ templateId }
		),
		method: HTTP_METHOD.GET,
	});
	return response.payload ?? [];
};

export const updateKycFieldBindings = async (templateId: string, bindings: FieldBindingDto[]) =>
	await makeRequest({
		endpoint: formatUnicorn(
			API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_TEMPLATEID_FIELD_BINDINGS_PUT,
			{ templateId }
		),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify({ bindings }),
	});

/** Programme-scoped, so it takes no template: every form in a programme binds the same points. */
export const getKycDataPointCatalogue = async (
	investorId?: string | null
): Promise<FieldBindingCatalogueRes> => {
	const response = await makeRequest<ApiV1.Response<FieldBindingCatalogueRes>>({
		endpoint: API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_FIELD_BINDING_CATALOGUE_GET,
		method: HTTP_METHOD.GET,
		...(investorId ? { queryString: { investorId } } : {}),
	});
	return (
		response.payload ?? {
			dataPoints: [],
			securities: [],
			scopedSamples: {},
			uncollectedRequirements: [],
		}
	);
};

export const previewKycFieldBindings = async (
	templateId: string,
	investorId: string
): Promise<{ fileId: string }> => {
	const response = await makeRequest<ApiV1.Response<{ fileId: string }>>({
		endpoint: formatUnicorn(
			API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_TEMPLATEID_FIELD_BINDINGS_PREVIEW_POST,
			{ templateId }
		),
		method: HTTP_METHOD.POST,
		queryString: { investorId },
	});
	return { fileId: response.payload?.fileId ?? '' };
};

/** The editable reflow document behind a Word KYC form. */
export const getKycDocxDocument = async (templateId: string): Promise<DocxFlowDocumentRes> => {
	const response = await makeRequest<ApiV1.Response<DocxFlowDocumentRes>>({
		endpoint: formatUnicorn(
			API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_TEMPLATEID_DOCX_DOCUMENT_GET,
			{ templateId }
		),
		method: HTTP_METHOD.GET,
	});
	return response.payload as DocxFlowDocumentRes;
};

export const saveKycDocxDocument = async (
	templateId: string,
	body: { document: unknown; bindings?: Record<string, string> }
): Promise<DocxFlowDocumentRes> => {
	const response = await makeRequest<ApiV1.Response<DocxFlowDocumentRes>>({
		endpoint: formatUnicorn(
			API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_TEMPLATEID_DOCX_DOCUMENT_PUT,
			{ templateId }
		),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(body),
	});
	return response.payload as DocxFlowDocumentRes;
};

/** Renders the form: field names in place, or one investor's real values. */
export const renderKycDocxTemplate = async (
	templateId: string,
	args: {
		/** Omit to render the fields view, with each field shown by name. */
		investorId?: string;
		/** The document as it stands in the editor. Omitted renders what is stored. */
		document?: unknown;
		bindings?: Record<string, string>;
		/** Hand-set answers for what the document's rules read. */
		scenario?: unknown;
	}
): Promise<PreviewRes> => {
	const { investorId, ...body } = args;
	const response = await makeRequest<ApiV1.Response<PreviewRes>>({
		endpoint: formatUnicorn(
			API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_TEMPLATEID_DOCX_RENDER_POST,
			{ templateId }
		),
		method: HTTP_METHOD.POST,
		...(investorId ? { queryString: { investorId } } : {}),
		body: JSON.stringify(body),
	});
	return response.payload as PreviewRes;
};

export const getSignatureSlots = async (
	dealId: string,
	programmeId: string,
	templateId: string
): Promise<GetSignatureSlotsRes> => {
	const response = await makeRequest<ApiV1.Response<GetSignatureSlotsRes>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_MASTER_DOCUMENTS_TEMPLATEID_SIGNATURE_SLOTS_GET,
			{ dealId, programmeId, templateId }
		),
		method: HTTP_METHOD.GET,
	});
	return response.payload ?? { slots: [], requiresPostDating: false, datePlacements: [] };
};

const buildMasterDocumentTemplatePath = (
	dealId: string,
	programmeId: string,
	templateId: string,
	suffix: string
): string =>
	formatUnicorn(
		`/deal/{dealId}/mip/{programmeId}/document-workflow/master-documents/{templateId}${suffix}`,
		{ dealId, programmeId, templateId }
	);

export const getFieldBindings = async (
	dealId: string,
	programmeId: string,
	templateId: string
): Promise<FieldBindingRes[]> => {
	const response = await makeRequest<ApiV1.Response<FieldBindingRes[]>>({
		endpoint: buildMasterDocumentTemplatePath(
			dealId,
			programmeId,
			templateId,
			'/field-bindings'
		),
		method: HTTP_METHOD.GET,
	});
	return response.payload ?? [];
};

export const updateFieldBindings = async (args: {
	dealId: string;
	programmeId: string;
	templateId: string;
	bindings: FieldBindingDto[];
}): Promise<void> => {
	await makeRequest<ApiV1.Response<void>>({
		endpoint: buildMasterDocumentTemplatePath(
			args.dealId,
			args.programmeId,
			args.templateId,
			'/field-bindings'
		),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify({ bindings: args.bindings }),
	});
};

export const previewFieldBindings = async (args: {
	dealId: string;
	programmeId: string;
	templateId: string;
	investorId: string;
}): Promise<PreviewRes> => {
	const response = await makeRequest<ApiV1.Response<PreviewRes>>({
		endpoint: buildMasterDocumentTemplatePath(
			args.dealId,
			args.programmeId,
			args.templateId,
			'/field-bindings/preview'
		),
		method: HTTP_METHOD.POST,
		queryString: { investorId: args.investorId },
	});
	if (response.payload == null) {
		throw new Error('Field-binding preview not generated');
	}
	return response.payload;
};

/** Omitting `investorId` renders the fields view, with each field shown by name in place. */
export const renderDocxTemplate = async (args: {
	dealId: string;
	programmeId: string;
	templateId: string;
	investorId?: string;
	/** The document as it stands in the editor. Omitted renders what is stored. */
	document?: unknown;
	bindings?: Record<string, string>;
	/** Hand-set answers for what the document's rules read. */
	scenario?: unknown;
}): Promise<PreviewRes> => {
	const response = await makeRequest<ApiV1.Response<PreviewRes>>({
		endpoint: buildMasterDocumentTemplatePath(
			args.dealId,
			args.programmeId,
			args.templateId,
			'/docx-render'
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify({
			...(args.document === undefined ? {} : { document: args.document }),
			...(args.bindings === undefined ? {} : { bindings: args.bindings }),
			...(args.scenario === undefined ? {} : { scenario: args.scenario }),
		}),
		...(args.investorId === undefined ? {} : { queryString: { investorId: args.investorId } }),
	});
	if (response.payload == null) {
		throw new Error('Word template render not generated');
	}
	return response.payload;
};

/**
 * The editable reflow document for a Word master document.
 *
 * Converted on first read by the backend, so this is also what creates it — a template uploaded
 * before the editor existed acquires a document the first time someone opens it.
 */
export const getDocxFlowDocument = async (args: {
	dealId: string;
	programmeId: string;
	templateId: string;
}): Promise<DocxFlowDocumentRes> => {
	const response = await makeRequest<ApiV1.Response<DocxFlowDocumentRes>>({
		endpoint: buildMasterDocumentTemplatePath(
			args.dealId,
			args.programmeId,
			args.templateId,
			'/docx-document'
		),
		method: HTTP_METHOD.GET,
	});
	if (response.payload == null) {
		throw new Error('Word document not available');
	}
	return response.payload;
};

export const saveDocxFlowDocument = async (args: {
	dealId: string;
	programmeId: string;
	templateId: string;
	document: unknown;
	/** Merged over what is stored, so adding a link can never remove one. */
	bindings?: Record<string, string>;
}): Promise<DocxFlowDocumentRes> => {
	const response = await makeRequest<ApiV1.Response<DocxFlowDocumentRes>>({
		endpoint: buildMasterDocumentTemplatePath(
			args.dealId,
			args.programmeId,
			args.templateId,
			'/docx-document'
		),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify({
			document: args.document,
			...(args.bindings === undefined ? {} : { bindings: args.bindings }),
		}),
	});
	if (response.payload == null) {
		throw new Error('Word document not saved');
	}
	return response.payload;
};

export const getDataPointCatalogue = async (
	dealId: string,
	programmeId: string,
	templateId: string,
	investorId: string
): Promise<FieldBindingCatalogueRes> => {
	const response = await makeRequest<ApiV1.Response<FieldBindingCatalogueRes>>({
		endpoint: buildMasterDocumentTemplatePath(
			dealId,
			programmeId,
			templateId,
			'/field-binding-catalogue'
		),
		method: HTTP_METHOD.GET,
		queryString: { investorId },
	});
	return (
		response.payload ?? {
			dataPoints: [],
			securities: [],
			scopedSamples: {},
			uncollectedRequirements: [],
		}
	);
};

export const getDealDocumentTemplates = async (
	dealId: string
): Promise<DealDocumentTemplateSummaryRes[]> => {
	const response = await makeRequest<ApiV1.Response<DealDocumentTemplateSummaryRes[]>>({
		endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_TEMPLATES_GET, { dealId }),
		method: HTTP_METHOD.GET,
	});
	return response.payload ?? [];
};

export const createDocumentWorkflowTemplate = async (args: {
	endpoint: string;
	body: CreateDocumentWorkflowTemplateDto;
}): Promise<{ id: string }> => {
	const response = await makeRequest<ApiV1.Response<{ id: string }>>({
		endpoint: args.endpoint,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args.body),
	});
	return response.payload ?? { id: '' };
};

export const archiveDocumentWorkflowTemplate = async (
	endpoint: string,
	body: UpdateArchivedDocumentWorkflowTemplateDto
) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint,
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(body),
	});
};

export const deleteDocumentWorkflowTemplate = async (endpoint: string) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint,
		method: HTTP_METHOD.DELETE,
	});
};

// `endpoint` is resolved by useMasterDocumentsEndpoints, so these serve both the programme and
// the organisation Programme Defaults routes.
export const bulkArchiveDocumentWorkflowTemplates = async (
	endpoint: string,
	body: BulkArchiveDocumentWorkflowTemplatesDto
): Promise<BulkOperationResult> => {
	return makeRequest<ApiV1.Response<BulkOperationResult>>({
		endpoint,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	}).then((response) => response.payload ?? { count: 0 });
};

export const bulkDeleteDocumentWorkflowTemplates = async (
	endpoint: string,
	body: BulkActionPayloadDto
): Promise<BulkDeleteDocumentWorkflowTemplatesRes> => {
	return makeRequest<ApiV1.Response<BulkDeleteDocumentWorkflowTemplatesRes>>({
		endpoint,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	}).then((response) => response.payload ?? { deletedCount: 0, failedCount: 0 });
};

type DownloadInvestmentsExcelRequest = DownloadInvestmentsExcelDto & {
	oneSchemaAllocationsTemplate?: boolean;
};

export const downloadInvestmentsExcel = async (
	args: DownloadInvestmentsExcelRequest
): Promise<Blob> => {
	return makeRequest<Blob>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_MIP_INVESTMENTS_DOWNLOAD_EXCEL_POST,
			{ mipId: args.mipId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	});
};

export const bulkUpdateExpectedTransactionType = async (
	mipId: string,
	body: BulkUpdateExpectedTransactionTypeDto
): Promise<ApiV1.Response<BulkUpdateExpectedTransactionTypeRes>> => {
	return makeRequest<ApiV1.Response<BulkUpdateExpectedTransactionTypeRes>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_MIP_INVESTMENTS_BULK_UPDATE_EXPECTED_TRANSACTION_TYPE_POST,
			{ mipId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	});
};

export const bulkSetSignerRequiresWitness = async (
	dealId: string,
	programmeId: string,
	instanceId: string,
	body: { roleIds: string[]; requiresWitness: boolean | null }
): Promise<ApiV1.Response<{ updatedCount: number }>> => {
	return await makeRequest<ApiV1.Response<{ updatedCount: number }>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_DOCUMENT_WORKFLOW_INSTANCES_INSTANCEID_SIGNERS_BY_ROLE_PATCH,
			{ dealId, programmeId, instanceId }
		),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(body),
	});
};

export const bulkSetSignerSecurityLevel = async (
	dealId: string,
	programmeId: string,
	instanceId: string,
	body: {
		roleIds: string[];
		securityLevel: DocumentSigningSecurityLevel | null;
	}
): Promise<ApiV1.Response<{ updatedCount: number }>> => {
	return await makeRequest<ApiV1.Response<{ updatedCount: number }>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_DOCUMENT_WORKFLOW_INSTANCES_INSTANCEID_SIGNERS_BY_ROLE_SECURITY_LEVEL_PATCH,
			{ dealId, programmeId, instanceId }
		),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(body),
	});
};

export const setSignerRequiresWitness = async (
	dealId: string,
	programmeId: string,
	instanceId: string,
	signerId: string,
	body: { requiresWitness: boolean | null }
): Promise<ApiV1.Response<{ updatedCount: number }>> => {
	return await makeRequest<ApiV1.Response<{ updatedCount: number }>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_DOCUMENT_WORKFLOW_INSTANCES_INSTANCEID_SIGNERS_SIGNERID_REQUIRES_WITNESS_PATCH,
			{ dealId, programmeId, instanceId, signerId }
		),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(body),
	});
};

export const setSignerSecurityLevel = async (
	dealId: string,
	programmeId: string,
	instanceId: string,
	signerId: string,
	body: {
		securityLevel: DocumentSigningSecurityLevel | null;
	}
): Promise<ApiV1.Response<{ updatedCount: number }>> => {
	return await makeRequest<ApiV1.Response<{ updatedCount: number }>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_DOCUMENT_WORKFLOW_INSTANCES_INSTANCEID_SIGNERS_SIGNERID_SECURITY_LEVEL_PATCH,
			{ dealId, programmeId, instanceId, signerId }
		),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(body),
	});
};
